/**
 * Stat Label Formatting — Single source of truth
 *
 * Maps raw stat type codes (canonical DB codes, Odds API market keys,
 * shorthand aliases) to human-readable display labels.
 *
 * Used by notificationDisplay.ts for notification card stat labels, and by getMarketKeyLabel below.
 */

import { isTeamSideMarket, marketFamily } from './marketKey';
import { marketKeyToStatType } from './statType';

export const STAT_LABELS: Record<string, string> = {
  // Canonical DB short codes
  pts: 'Points',
  reb: 'Rebounds',
  ast: 'Assists',
  '3pm': '3-Pointers',
  fg3m: '3-Pointers',
  threes: '3-Pointers',
  stl: 'Steals',
  blk: 'Blocks',
  to: 'Turnovers',
  turnover: 'Turnovers',
  fgm: 'Field Goals',
  ftm: 'Free Throws',

  // Combo short codes
  pra: 'PRA',
  pr: 'Pts+Reb',
  pa: 'Pts+Ast',
  ra: 'Reb+Ast',
  bs: 'Blk+Stl',
  dd: 'Double-Double',
  td: 'Triple-Double',

  // Underscore combo aliases
  pts_reb_ast: 'PRA',
  pts_reb: 'Pts+Reb',
  pts_ast: 'Pts+Ast',
  reb_ast: 'Reb+Ast',
  blk_stl: 'Blk+Stl',
  points_rebounds_assists: 'PRA',
  points_rebounds: 'Pts+Reb',
  points_assists: 'Pts+Ast',
  rebounds_assists: 'Reb+Ast',
  blocks_steals: 'Blk+Stl',
  double_double: 'Double-Double',
  triple_double: 'Triple-Double',

  // Special prop types
  first_basket: 'First Basket',
  first_team_basket: 'First Team Basket',

  // Props API market names (the Odds API keys without "player_")
  points: 'Points',
  rebounds: 'Rebounds',
  assists: 'Assists',
  blocks: 'Blocks',
  steals: 'Steals',
  turnovers: 'Turnovers',
  field_goals: 'Field Goals',
  frees_made: 'Free Throws',

  // Game-level types
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',

  // Odds API long-form market keys
  player_points: 'Points',
  player_rebounds: 'Rebounds',
  player_assists: 'Assists',
  player_threes: '3-Pointers',
  player_blocks: 'Blocks',
  player_steals: 'Steals',
  player_turnovers: 'Turnovers',
  player_points_rebounds_assists: 'PRA',
  player_points_rebounds: 'Pts+Reb',
  player_points_assists: 'Pts+Ast',
  player_rebounds_assists: 'Reb+Ast',
  player_blocks_steals: 'Blk+Stl',
  player_double_double: 'Double-Double',
  player_triple_double: 'Triple-Double',
  player_first_basket: 'First Basket',
  player_first_team_basket: 'First Team Basket',
  player_field_goals: 'Field Goals',
  player_free_throws: 'Free Throws',
  player_frees_made: 'Free Throws',

  // Alternate player prop market keys (exact line = win, not push)
  player_points_alternate: 'Points+',
  player_rebounds_alternate: 'Rebounds+',
  player_assists_alternate: 'Assists+',
  player_threes_alternate: '3-Pointers+',
  player_steals_alternate: 'Steals+',
  player_blocks_alternate: 'Blocks+',
  player_turnovers_alternate: 'Turnovers+',
  player_field_goals_alternate: 'Field Goals+',
  player_blocks_steals_alternate: 'Blk+Stl+',
  player_points_rebounds_assists_alternate: 'PRA+',
  player_points_rebounds_alternate: 'Pts+Reb+',
  player_points_assists_alternate: 'Pts+Ast+',
  player_rebounds_assists_alternate: 'Reb+Ast+',

  // Q1 player prop market keys
  player_points_q1: 'Q1 Points',
  player_rebounds_q1: 'Q1 Rebounds',
  player_assists_q1: 'Q1 Assists',

  // Team total market keys
  team_total: 'Team Total',
  team_totals: 'Team Total',

  // Game-level market keys
  h2h: 'Moneyline',
  spreads: 'Spread',
  totals: 'Total',

  // Game-level alternate market keys
  alternate_spreads: 'Alt Spread',
  alternate_totals: 'Alt Total',
  alternate_team_totals: 'Alt Team Total',

  // Half game market keys
  h2h_h1: '1H Moneyline',
  h2h_h2: '2H Moneyline',
  spreads_h1: '1H Spread',
  spreads_h2: '2H Spread',
  totals_h1: '1H Total',
  totals_h2: '2H Total',
  team_totals_h1: '1H Team Total',
  team_totals_h2: '2H Team Total',
  alternate_spreads_h1: '1H Alt Spread',
  alternate_spreads_h2: '2H Alt Spread',
  alternate_totals_h1: '1H Alt Total',
  alternate_totals_h2: '2H Alt Total',
  alternate_team_totals_h1: '1H Alt Team Total',
  alternate_team_totals_h2: '2H Alt Team Total',

  // Quarter game market keys
  h2h_q1: 'Q1 Moneyline',
  h2h_q2: 'Q2 Moneyline',
  h2h_q3: 'Q3 Moneyline',
  h2h_q4: 'Q4 Moneyline',
  spreads_q1: 'Q1 Spread',
  spreads_q2: 'Q2 Spread',
  spreads_q3: 'Q3 Spread',
  spreads_q4: 'Q4 Spread',
  totals_q1: 'Q1 Total',
  totals_q2: 'Q2 Total',
  totals_q3: 'Q3 Total',
  totals_q4: 'Q4 Total',
  team_totals_q1: 'Q1 Team Total',
  team_totals_q2: 'Q2 Team Total',
  team_totals_q3: 'Q3 Team Total',
  team_totals_q4: 'Q4 Team Total',
};

/** A leg's text with its period or alternate line named, which buildPropString's "Lakers ML" leaves out. */
export function legTextWithMarket(text: string, marketKey: string | null | undefined): string {
  if (!marketKey || !isTeamSideMarket(marketKey) || marketFamily(marketKey) === marketKey) {
    return text;
  }
  const marketName = formatStatLabel(marketKey);
  if (text.toLowerCase().includes(marketName.toLowerCase())) return text;
  return / ML$/.test(text) ? text.replace(/ ML$/, ` ${marketName}`) : `${text} ${marketName}`;
}

/** A moneyline or spread leg named by the team it backs: "LAL ML", "LAL +3.5", with any period. */
export function teamSideLegText(
  team: string,
  line: number | null,
  marketKey: string | null | undefined,
): string {
  const side = line === null ? 'ML' : `${line > 0 ? '+' : ''}${line}`;
  return legTextWithMarket(`${team} ${side}`, marketKey);
}

/**
 * Convert a raw stat type code to a human-readable label.
 * Case-insensitive. Falls back to title-casing the code if no mapping exists.
 */
export function formatStatLabel(rawStatType: string): string {
  if (!rawStatType) return '';
  const key = rawStatType.toLowerCase().trim().replace(/\+/g, '_');
  if (STAT_LABELS[key]) return STAT_LABELS[key];
  // Fallback: title-case with underscores/hyphens as spaces
  return key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Regex patterns for normalizing stat type abbreviations embedded in mixed strings.
// Combo patterns must come before single-stat patterns to avoid partial matches.
// Separators match: space, +, _, or combinations (e.g. "pts_reb_ast", "pts+reb+ast")
const PROP_REPLACEMENTS: Array<[RegExp, string]> = [
  // 3-stat combos (must be first)
  [/points[\s_+]*rebounds[\s_+]*assists/gi, 'PRA'],
  [/pts[\s_+]*reb[\s_+]*ast/gi, 'PRA'],
  // 2-stat combos
  [/points[\s_+]*rebounds/gi, 'Pts+Reb'],
  [/pts[\s_+]*reb(?![\s_+]*ast)/gi, 'Pts+Reb'],
  [/points[\s_+]*assists/gi, 'Pts+Ast'],
  [/pts[\s_+]*ast/gi, 'Pts+Ast'],
  [/rebounds[\s_+]*assists/gi, 'Reb+Ast'],
  [/reb[\s_+]*ast/gi, 'Reb+Ast'],
  [/blocks[\s_+]*steals/gi, 'Blk+Stl'],
  [/blk[\s_+]*stl/gi, 'Blk+Stl'],
  // Shorthand combo codes (e.g. "Over 21.5 pa" → "Over 21.5 Pts+Ast")
  [/\bpra\b/gi, 'PRA'],
  [/\bpr\b/gi, 'Pts+Reb'],
  [/\bpa\b/gi, 'Pts+Ast'],
  [/\bra\b/gi, 'Reb+Ast'],
  [/\bbs\b/gi, 'Blk+Stl'],
  // Single stats (word-boundary to avoid partial matches)
  [/\bpts\b/gi, 'Points'],
  [/\breb\b/gi, 'Rebounds'],
  [/\bast\b/gi, 'Assists'],
  [/\bblk\b/gi, 'Blocks'],
  [/\bstl\b/gi, 'Steals'],
  [/\b3pm\b/gi, '3-Pointers'],
  [/\bfg3m\b/gi, '3-Pointers'],
  [/\bpoints\b/gi, 'Points'],
  [/\brebounds\b/gi, 'Rebounds'],
  [/\bassists\b/gi, 'Assists'],
  [/\bblocks\b/gi, 'Blocks'],
  [/\bsteals\b/gi, 'Steals'],
  [/\bthrees\b/gi, '3-Pointers'],
];

/**
 * Format a full prop display string (e.g. "Over 11.5 pts_reb_ast" → "Over 11.5 PRA").
 * Handles:
 *   - Bare stat keys: "pts_ast" → "Pts+Ast"
 *   - Mixed strings with numbers: "Over 25.5 points" → "Over 25.5 Points"
 *   - Already formatted: "Over 25.5 PRA" → unchanged
 *   - _alternate suffix: stripped automatically
 */
export function formatPropString(prop: string): string {
  if (!prop) return '';

  // Strip _alternate suffix (alternate props are indicated by line format, not label)
  const stripped = prop.replace(/_alternate/gi, '');

  // Try direct lookup first (handles bare stat keys)
  // Normalize + to _ for consistent key matching (e.g. "Points+Rebounds" → "points_rebounds")
  const normalized = stripped.toLowerCase().replace(/\+/g, '_');
  const directMatch = STAT_LABELS[normalized];
  if (directMatch) return directMatch;

  // Fallback: regex substitution for mixed strings
  // Normalize a combo "+" (pts+reb) to a space so the patterns match; a "+" touching a
  // number is a sign ("Liberty +6.5") or the alternate "25+" format and stays.
  let result = stripped.replace(/([a-z])\+(?=[a-z])/gi, '$1 ');
  for (const [pattern, replacement] of PROP_REPLACEMENTS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/** Display order for a player card's season stats. */
export const SEASON_STAT_ORDER = [
  'pts',
  'reb',
  'ast',
  'fg3m',
  'blk',
  'stl',
  'turnover',
  'bs',
  'pra',
  'pr',
  'pa',
  'ra',
  'games',
] as const;

/**
 * A stats object's entries in SEASON_STAT_ORDER, unknown keys last. Object order is
 * not stable: a card saved as JSONB comes back with its keys sorted by length.
 */
export function orderSeasonStats<V>(stats: Record<string, V>): Array<[string, V]> {
  const order: readonly string[] = SEASON_STAT_ORDER;
  const rank = (key: string) => {
    const i = order.indexOf(key);
    return i === -1 ? order.length : i;
  };
  return Object.entries(stats).sort(([a], [b]) => rank(a) - rank(b));
}

/**
 * Get human-readable display label for a marketKey
 * This is a convenience wrapper that converts marketKey → statType → label
 */
export function getMarketKeyLabel(marketKey: string): string {
  if (!marketKey) return String(marketKey);

  const customLabels: Record<string, string> = {
    // Standard player props
    player_points: 'points',
    player_rebounds: 'rebounds',
    player_assists: 'assists',
    player_threes: 'threes',
    player_blocks: 'blocks',
    player_steals: 'steals',
    player_turnovers: 'turnovers',
    player_field_goals: 'field goals',
    player_frees_made: 'free throws',
    player_blocks_steals: 'blocks+steals',
    player_points_rebounds_assists: 'PRA',
    player_points_rebounds: 'points+rebounds',
    player_points_assists: 'points+assists',
    player_rebounds_assists: 'rebounds+assists',
    player_double_double: 'double-double',
    player_triple_double: 'triple-double',
    player_first_basket: 'first basket',
    player_first_team_basket: 'first team basket',
    // Alternate player props
    player_points_alternate: 'points+',
    player_rebounds_alternate: 'rebounds+',
    player_assists_alternate: 'assists+',
    player_threes_alternate: 'threes+',
    player_steals_alternate: 'steals+',
    player_blocks_alternate: 'blocks+',
    player_turnovers_alternate: 'turnovers+',
    player_field_goals_alternate: 'field goals+',
    player_blocks_steals_alternate: 'blocks+steals+',
    player_points_rebounds_assists_alternate: 'PRA+',
    player_points_rebounds_alternate: 'points+rebounds+',
    player_points_assists_alternate: 'points+assists+',
    player_rebounds_assists_alternate: 'rebounds+assists+',
    // Q1 player props
    player_points_q1: 'Q1 points',
    player_rebounds_q1: 'Q1 rebounds',
    player_assists_q1: 'Q1 assists',
    // Game markets
    h2h: 'moneyline',
    spreads: 'spread',
    totals: 'total',
    alternate_spreads: 'alt spread',
    alternate_totals: 'alt total',
    // Team totals
    team_totals: 'team total',
    alternate_team_totals: 'alt team total',
    // Half game markets
    h2h_h1: '1H moneyline',
    h2h_h2: '2H moneyline',
    spreads_h1: '1H spread',
    spreads_h2: '2H spread',
    totals_h1: '1H total',
    totals_h2: '2H total',
    team_totals_h1: '1H team total',
    team_totals_h2: '2H team total',
    alternate_spreads_h1: '1H alt spread',
    alternate_spreads_h2: '2H alt spread',
    alternate_totals_h1: '1H alt total',
    alternate_totals_h2: '2H alt total',
    alternate_team_totals_h1: '1H alt team total',
    alternate_team_totals_h2: '2H alt team total',
    // Quarter game markets
    h2h_q1: 'Q1 moneyline',
    h2h_q2: 'Q2 moneyline',
    h2h_q3: 'Q3 moneyline',
    h2h_q4: 'Q4 moneyline',
    spreads_q1: 'Q1 spread',
    spreads_q2: 'Q2 spread',
    spreads_q3: 'Q3 spread',
    spreads_q4: 'Q4 spread',
    totals_q1: 'Q1 total',
    totals_q2: 'Q2 total',
    totals_q3: 'Q3 total',
    totals_q4: 'Q4 total',
    team_totals_q1: 'Q1 team total',
    team_totals_q2: 'Q2 team total',
    team_totals_q3: 'Q3 team total',
    team_totals_q4: 'Q4 team total',
  };

  const lower = marketKey.toLowerCase();
  if (customLabels[lower]) {
    return customLabels[lower];
  }

  // Fallback to converting via statType
  const statType = marketKeyToStatType(marketKey);
  return statType ? formatStatLabel(statType) : marketKey;
}

/** Short stat label for prop pills (e.g., "PTS", "REB", "PRA") */
export function shortStatLabel(statType: string): string {
  const label = formatStatLabel(statType);
  const abbrevMap: Record<string, string> = {
    Points: 'PTS',
    Rebounds: 'REB',
    Assists: 'AST',
    Steals: 'STL',
    Blocks: 'BLK',
    '3-Pointers': '3PM',
    Turnovers: 'TO',
    'Free Throws': 'FT',
    'Field Goals': 'FG',
    'Double-Double': 'DD',
    'Triple-Double': 'TD',
  };
  return abbrevMap[label] || label.toUpperCase();
}
